package com.nonopn.skydome.common.app.pages.main.view.map

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.Divider
import androidx.compose.material.OutlinedButton
import androidx.compose.material.icons.Icons
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp

import com.nonopn.icons.Certificate
import com.nonopn.icons.Density
import com.nonopn.icons.Pin
import com.nonopn.skydome.common.app.home.HotPreviewApp
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.navigation.RouteCertNsg
import com.nonopn.skydome.common.app.navigation.RouteEstimateNew
import com.nonopn.skydome.common.app.navigation.RouteKeraunicReport
import com.nonopn.skydome.common.app.navigation.RouterCertNsg
import com.nonopn.skydome.common.app.navigation.RouterEstimateNew
import com.nonopn.skydome.common.app.navigation.RouterEstimateNewAdmin
import com.nonopn.skydome.common.app.navigation.RouterKeraunicReport
import com.nonopn.skydome.common.app.pages.main.LocalMapScreenModel
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.cert_nsg_title
import com.nonopn.skywatcher.common.app.res.keraunic_report_title
import com.nonopn.skywatcher.common.app.res.select
import com.nonopn.skywatcher.design.local.LocalFontSizes
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.localized
import com.nonopn.skywatcher.design.widgets.DefaultCard

import de.drick.compose.hotpreview.HotPreview
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.compose.widgets.VectorIcon

@Composable
fun ShowProject(
    modifier: Modifier,
    project: Project,
    onProjectSelected: () -> Unit
) {
    val appModel = LocalApp.current
    val mapModel = LocalMapScreenModel.current
    val mapState by mapModel.states.collectAsState()
    val appState by appModel.states.collectAsState()
    val isAdmin = appState.apiProvider?.apiAdmin != null
    var showNoCoverageDialog by remember { mutableStateOf(false) }

    if (showNoCoverageDialog) {
        NoProbeCoverageDialog(
            onAddProbe = {
                showNoCoverageDialog = false
                appModel.show(
                    // An administrator stays in the administrator screens (and menu)
                    if (appModel.states.value.isAdminMode) {
                        RouterEstimateNewAdmin.navigateTo()
                    } else {
                        RouterEstimateNew.navigateTo()
                    }
                )
            }.takeIf { isAdmin },
            onDismiss = { showNoCoverageDialog = false }
        )
    }

    DefaultCard(
        modifier = modifier,
        backgroundColor = AppColor.White,
        columnModifier = Modifier.padding(12.dp)
    ) {
        Column(
            Modifier.fillMaxWidth(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            TextNormal(
                text = project.name,
                fontSize = LocalFontSizes.current.map.callOutBig,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.Center,
                color = AppColor.StrikeText
            )

            Spacer(Modifier.height(4.dp))

            Divider(
                Modifier.fillMaxWidth().padding(horizontal = 8.dp),
                color = AppColor.GrayExtraLight
            )

            Spacer(Modifier.height(4.dp))

            OutlinedButton(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp),
                onClick = {
                    if (isWithinProbeCoverage(
                            project.latitude,
                            project.longitude,
                            mapState.probes
                        )
                    ) {
                        appModel.show(
                            RouterKeraunicReport.navigateTo(
                                project.toKeraunicReportRoute()
                            )
                        )
                    } else {
                        showNoCoverageDialog = true
                    }
                }
            ) {
                VectorIcon(
                    modifier = Modifier.size(18.dp),
                    imageVector = Icons.Rounded.Density,
                    color = AppColor.StrikePrimary
                )
                Spacer(Modifier.width(8.dp))
                TextNormal(
                    text = Res.string.keraunic_report_title.localized(),
                    color = AppColor.StrikeText,
                    textAlign = TextAlign.Center
                )
            }

            OutlinedButton(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp),
                onClick = {
                    appModel.show(
                        RouterCertNsg.navigateTo(
                            project.toCertNsgRoute()
                        )
                    )
                }
            ) {
                VectorIcon(
                    modifier = Modifier.size(18.dp),
                    imageVector = Icons.Rounded.Certificate,
                    color = AppColor.StrikePrimary
                )
                Spacer(Modifier.width(8.dp))
                TextNormal(
                    text = Res.string.cert_nsg_title.localized(),
                    color = AppColor.StrikeText,
                    textAlign = TextAlign.Center
                )
            }

            OutlinedButton(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 4.dp),
                onClick = onProjectSelected
            ) {
                VectorIcon(
                    modifier = Modifier.size(18.dp),
                    imageVector = Icons.Rounded.Pin,
                    color = AppColor.StrikePrimary
                )
                Spacer(Modifier.width(8.dp))
                TextNormal(
                    text = Res.string.select.localized(),
                    color = AppColor.StrikeText,
                    textAlign = TextAlign.Center
                )
            }
        }
    }
}

private fun Project.toKeraunicReportRoute() = RouteKeraunicReport(
    projectUuid = uuid,
    projectName = name,
    lat = latitude,
    lon = longitude
)

private fun Project.toCertNsgRoute() = RouteCertNsg(
    projectUuid = uuid,
    projectName = name,
    lat = latitude,
    lon = longitude
)

@Composable
@HotPreview(widthDp = 250, heightDp = 220, darkMode = true)
@HotPreview(widthDp = 250, heightDp = 220, darkMode = false)
private fun ShowProjectPreview() {
    HotPreviewApp(Modifier.fillMaxSize(), isDarkTheme = isSystemInDarkTheme()) {
        ShowProject(
            Modifier.fillMaxSize(),
            Project(
                latitude = 0.0,
                longitude = 0.0,
                name = "Example Project",
                uuid = ""
            )
        ) { /* nothing */ }
    }
}
